// src/utils/viewport.js
//
// Keeps CSS variables in sync with the *visual* viewport so layouts stay
// stable when iOS Safari shows/hides its toolbar or the on-screen keyboard.
//
// Layered defences against iOS Safari quirks (the "phantom keyboard" bug
// where --app-height stays stuck at a keyboard-up value on Landing/Empty/
// Score, leaving a pink dead zone at the bottom of the screen):
//
//   A/G. When no input is focused the keyboard cannot legitimately be eating
//        the viewport — clamp height to >= window.innerHeight and zero the
//        offsets, so a stale reading can never persist into a non-input screen.
//   B.   focusout listener: programmatic blur() does not reliably fire
//        visualViewport.resize on iOS — re-sample after the keyboard
//        animation settles.
//   C.   visibilitychange + pageshow: a tab returning from background or
//        BFCache restore can carry stale values.
//   D.   Public recomputeViewport() so App.js can kick a recompute on every
//        screen change (with 3 samples: now, next frame, +250ms).

const isInputFocused = () => {
  const ae = document.activeElement;
  if (!ae) return false;
  const tag = ae.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || ae.isContentEditable === true;
};

const computeAndApply = () => {
  const vv = window.visualViewport;
  const layoutH = window.innerHeight || 0;
  const visualH = vv ? vv.height : layoutH;
  const offsetTop = vv ? vv.offsetTop : 0;
  const offsetBottom = Math.max(0, layoutH - visualH - offsetTop);

  // Fix A / G — refuse to shrink (or apply non-zero offsets) unless an
  // input/textarea/contenteditable is actually focused. This single rule
  // makes a stale keyboard-up --app-height impossible on Landing etc.
  const inputFocused = isInputFocused();
  const effectiveH      = inputFocused ? visualH      : Math.max(visualH, layoutH);
  const effOffsetTop    = inputFocused ? offsetTop    : 0;
  const effOffsetBottom = inputFocused ? offsetBottom : 0;

  const el = document.documentElement;
  el.style.setProperty('--app-height', `${effectiveH}px`);
  el.style.setProperty('--vv-offset-top', `${effOffsetTop}px`);
  el.style.setProperty('--vv-offset-bottom', `${effOffsetBottom}px`);
};

// Fix D — public recompute. Samples now + next animation frame + +250ms,
// covering immediate / post-layout / post-keyboard-animation timings.
export function recomputeViewport() {
  computeAndApply();
  try { window.requestAnimationFrame(computeAndApply); } catch {}
  window.setTimeout(computeAndApply, 250);
}

export function installViewportVars() {
  computeAndApply();

  window.addEventListener('resize', computeAndApply);
  window.addEventListener('orientationchange', computeAndApply);

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', computeAndApply);
    window.visualViewport.addEventListener('scroll', computeAndApply);
  }

  // Fix B — programmatic blur does not always fire visualViewport.resize on
  // iOS. focusout fires reliably; re-sample twice so we cover the keyboard
  // dismissal animation (which lasts ~250–300ms).
  const onFocusOut = () => {
    window.setTimeout(computeAndApply, 60);
    window.setTimeout(computeAndApply, 320);
  };
  document.addEventListener('focusout', onFocusOut, true);

  // Fix C — tab returning from background / BFCache restore.
  const onVisibility = () => {
    if (document.visibilityState === 'visible') recomputeViewport();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', recomputeViewport);

  return () => {
    window.removeEventListener('resize', computeAndApply);
    window.removeEventListener('orientationchange', computeAndApply);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', computeAndApply);
      window.visualViewport.removeEventListener('scroll', computeAndApply);
    }
    document.removeEventListener('focusout', onFocusOut, true);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pageshow', recomputeViewport);
  };
}
